// Constants
export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_CATEGORY = 'UTILITY';
export const COMPONENT_TYPES = {
  HEADER: 'HEADER',
  BODY: 'BODY',
  BUTTONS: 'BUTTONS',
};
export const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

export const findComponentByType = (template, type) =>
  template.components?.find(component => component.type === type);

export const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

export const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

export const replaceTemplateVariables = (templateText, processedParams) => {
  return templateText.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.body?.[variableKey] || `{{${variable}}}`;
  });
};

export const buildTemplateParameters = (template, hasMediaHeaderValue) => {
  const allVariables = {};

  const bodyComponent = findComponentByType(template, COMPONENT_TYPES.BODY);
  const headerComponent = findComponentByType(template, COMPONENT_TYPES.HEADER);

  if (!bodyComponent) return allVariables;

  const templateString = bodyComponent.text;

  // Process body variables
  const matchedVariables = templateString.match(/{{([^}]+)}}/g);
  if (matchedVariables) {
    allVariables.body = {};
    matchedVariables.forEach(variable => {
      const key = processVariable(variable);
      allVariables.body[key] = '';
    });
  }

  if (hasMediaHeaderValue) {
    if (!allVariables.header) allVariables.header = {};
    allVariables.header.media_url = '';
    allVariables.header.media_type = headerComponent.format.toLowerCase();

    // For document templates, include media_name field for filename support
    if (headerComponent.format.toLowerCase() === 'document') {
      allVariables.header.media_name = '';
    }
  }

  // Process button variables
  const buttonComponents = template.components.filter(
    component => component.type === COMPONENT_TYPES.BUTTONS
  );

  buttonComponents.forEach(buttonComponent => {
    if (buttonComponent.buttons) {
      buttonComponent.buttons.forEach((button, index) => {
        // Handle URL buttons with variables
        if (button.type === 'URL' && button.url && button.url.includes('{{')) {
          const buttonVars = button.url.match(/{{([^}]+)}}/g) || [];
          if (buttonVars.length > 0) {
            if (!allVariables.buttons) allVariables.buttons = [];
            allVariables.buttons[index] = {
              type: 'url',
              parameter: '',
              url: button.url,
              variables: buttonVars.map(v => processVariable(v)),
            };
          }
        }

        // Handle copy code buttons
        if (button.type === 'COPY_CODE') {
          if (!allVariables.buttons) allVariables.buttons = [];
          allVariables.buttons[index] = {
            type: 'copy_code',
            parameter: '',
          };
        }
      });
    }
  });

  return allVariables;
};

export const initializeTemplateParameters = (
  template,
  existingParams = {},
  hasMediaHeaderValue = false
) => {
  const baseParams = buildTemplateParameters(template, hasMediaHeaderValue);
  const mergedParams = {
    ...baseParams,
    ...existingParams,
  };

  if (baseParams.body || existingParams.body) {
    mergedParams.body = {
      ...(baseParams.body || {}),
      ...(existingParams.body || {}),
    };
  }

  if (baseParams.header || existingParams.header) {
    mergedParams.header = {
      ...(baseParams.header || {}),
      ...(existingParams.header || {}),
    };
  }

  if (
    Array.isArray(baseParams.buttons) ||
    Array.isArray(existingParams.buttons)
  ) {
    const baseButtons = Array.isArray(baseParams.buttons)
      ? baseParams.buttons
      : [];
    const existingButtons = Array.isArray(existingParams.buttons)
      ? existingParams.buttons
      : [];
    const buttonCount = Math.max(baseButtons.length, existingButtons.length);

    mergedParams.buttons = Array.from({ length: buttonCount }, (_, index) => ({
      ...(baseButtons[index] || {}),
      ...(existingButtons[index] || {}),
    }));
  }

  return mergedParams;
};

export const templateParametersComplete = (template, processedParams = {}) => {
  if (!template?.components?.length) return false;

  const headerComponent = findComponentByType(template, COMPONENT_TYPES.HEADER);
  const hasMediaHeader = MEDIA_FORMATS.includes(headerComponent?.format);
  const requiredParams = buildTemplateParameters(template, hasMediaHeader);

  if (requiredParams.header?.media_url && !processedParams.header?.media_url) {
    return false;
  }

  if (requiredParams.body) {
    const hasMissingBodyValues = Object.keys(requiredParams.body).some(
      key => !processedParams.body?.[key]
    );
    if (hasMissingBodyValues) {
      return false;
    }
  }

  if (Array.isArray(requiredParams.buttons)) {
    const hasMissingButtonValues = requiredParams.buttons.some((_, index) => {
      return !processedParams.buttons?.[index]?.parameter;
    });
    if (hasMissingButtonValues) {
      return false;
    }
  }

  return true;
};
